import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { formatRp, formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Calendar,
  TrendingUp, TrendingDown, PiggyBank, Percent, CircleDollarSign,
  BarChart3, PieChart as PieIcon, LayoutDashboard, Wallet2, HandCoins,
} from 'lucide-react';
import { StatusBadge } from '@/pages/HomePage';
import IncomeDialog from '@/components/IncomeDialog';
import ExpenseDialog from '@/components/ExpenseDialog';
import TransactionTable from '@/components/TransactionTable';
import WorkspaceCharts from '@/components/WorkspaceCharts';
import MutasiDanaSection from '@/components/MutasiDanaSection';
import ProfitSharingSection from '@/components/ProfitSharingSection';

const WORKSPACE_TABS = ['dashboard', 'recap', 'mutasi', 'charts', 'profit-sharing'];

export default function ProjectWorkspacePage() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [dash, setDash] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIncome, setOpenIncome] = useState(false);
  const [openExpense, setOpenExpense] = useState(false);
  const [editingTx, setEditingTx] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(WORKSPACE_TABS.includes(initialTab) ? initialTab : 'dashboard');
  const [recentIncome, setRecentIncome] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [pRes, dRes, cRes, incomeRes] = await Promise.all([
        api.get(`/projects/${pid}`),
        api.get(`/projects/${pid}/dashboard`),
        api.get(`/projects/${pid}/charts`),
        api.get(`/projects/${pid}/transactions`, { params: { type: 'income', sort: 'date_desc', page: 1, limit: 100 } }),
      ]);
      setProject(pRes.data);
      setDash(dRes.data);
      setCharts(cRes.data);
      setRecentIncome(incomeRes.data.items || []);
    } catch (e) {
      toast.error('Gagal memuat project');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [pid, navigate]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    const requested = searchParams.get('tab');
    if (WORKSPACE_TABS.includes(requested) && requested !== activeTab) setActiveTab(requested);
  }, [searchParams, activeTab]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'dashboard' ? {} : { tab }, { replace: true });
  };

  const onTxSaved = () => { loadAll(); setReloadKey((k) => k + 1); };

  const openEditTx = (tx) => {
    setEditingTx(tx);
    if (tx.type === 'income') setOpenIncome(true);
    else setOpenExpense(true);
  };

  const closeIncome = (o) => { setOpenIncome(o); if (!o) setEditingTx(null); };
  const closeExpense = (o) => { setOpenExpense(o); if (!o) setEditingTx(null); };

  const doExport = async (kind) => {
    try {
      const token = localStorage.getItem('fpccs_token');
      const url = `${API_BASE}/projects/${pid}/export/${kind}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
      a.download = `FPCCS_${(project?.name || 'Project').replace(/\s+/g,'_')}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Laporan ${kind.toUpperCase()} berhasil diunduh`);
    } catch (e) { toast.error('Gagal mengekspor laporan'); }
  };

  if (loading || !project || !dash) {
    return (
      <div className="space-y-4 animate-in-up">
        <Skeleton className="h-24 rounded-2xl" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[...Array(8)].map((_,i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
      </div>
    );
  }

  const nilaiRab = Number(dash.total_budget) || 0;
  const nilaiKeuntungan = Number(dash.sisa_budget) || 0;
  const pctKeuntungan = nilaiRab > 0 ? (nilaiKeuntungan / nilaiRab) * 100 : 0;

  return (
    <div className="space-y-6 animate-in-up">
      {/* Back */}
      <div>
        <Link to="/" data-testid="btn-back-home" className="inline-flex items-center gap-1.5 text-xs text-muted-fpccs hover:text-[var(--fpccs-text)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Project
        </Link>
      </div>

      {/* Project Header */}
      <div className="card-fpccs p-5 sm:p-6 strip-primary">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-subtle-fpccs">{project.client_name || 'Tanpa Client'}</div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-0.5">{project.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-fpccs">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {project.location || '-'}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Mulai {formatDate(project.start_date)}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Est. Selesai {formatDate(project.end_date)}</span>
              <StatusBadge status={project.status} />
            </div>
            {project.description && <p className="mt-3 text-sm text-muted-fpccs max-w-2xl">{project.description}</p>}
          </div>
          <div className="md:w-72 shrink-0">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-[11px] text-subtle-fpccs uppercase tracking-wider">Progress</span>
              <span className="text-sm font-semibold">{project.progress || 0}%</span>
            </div>
            <div className="h-2 rounded-full bg-[var(--fpccs-surface-3)] overflow-hidden">
              <div className="h-full bg-[var(--fpccs-primary)]" style={{ width: `${project.progress || 0}%` }} />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] text-subtle-fpccs">Nilai RAB</div>
                  <div className="text-sm font-semibold font-mono">{formatRp(dash.total_budget, { compact: true })}</div>
                </div>
                <div>
                  <div className="text-[11px] text-subtle-fpccs">Nilai Keuntungan</div>
                  <div className={`text-sm font-semibold font-mono ${dash.sisa_budget < 0 ? 'text-[var(--fpccs-danger)]' : 'text-[var(--fpccs-success)]'}`}>{formatRp(dash.sisa_budget, { compact: true })}</div>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* Workspace tabs */}
      <section className="card-fpccs overflow-hidden">
        <div
          role="tablist"
          aria-label="Menu workspace project"
          className="relative z-0 flex min-h-[60px] max-w-full items-stretch overflow-x-auto overflow-y-hidden overscroll-x-contain border-b border-[var(--fpccs-border)] bg-[var(--fpccs-glass-strong)] px-2 backdrop-blur-xl sm:px-4 [scrollbar-width:thin]"
        >
          <WorkspaceTab active={activeTab === 'dashboard'} icon={LayoutDashboard} label="Dashboard Project" onClick={() => selectTab('dashboard')} />
          <WorkspaceTab active={activeTab === 'recap'} icon={PieIcon} label="Rekap Pengeluaran" onClick={() => selectTab('recap')} />
          <WorkspaceTab active={activeTab === 'mutasi'} icon={Wallet2} label="Mutasi Dana" onClick={() => selectTab('mutasi')} />
          <WorkspaceTab active={activeTab === 'charts'} icon={BarChart3} label="Grafik" onClick={() => selectTab('charts')} />
          <WorkspaceTab active={activeTab === 'profit-sharing'} icon={HandCoins} label="Profit Sharing" onClick={() => selectTab('profit-sharing')} />
        </div>

        <div className="p-4 sm:p-5 lg:p-6">
          {activeTab === 'dashboard' && (
            <div role="tabpanel" className="grid gap-3 sm:gap-4 lg:grid-cols-2 animate-in-up">
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                <KpiCard testid="kpi-total-budget" label="Nilai RAB" value={formatRp(dash.total_budget)} icon={CircleDollarSign} tone="neutral" />
                <KpiCard testid="kpi-total-pengeluaran" label="Total Pengeluaran" value={formatRp(dash.total_expense)} icon={TrendingDown} tone="danger" />
                <IncomeSummaryCard items={recentIncome} total={dash.total_income} />
              </div>

              <div className="grid gap-3 sm:grid-cols-3 sm:gap-4">
                <KpiCard testid="kpi-sisa-budget" label="Nilai Keuntungan" value={formatRp(dash.sisa_budget)} icon={PiggyBank} tone={dash.sisa_budget < 0 ? 'danger' : 'success'} className="sm:col-span-2" />
                <KpiCard testid="kpi-pct-keuntungan" label="% Keuntungan" value={`${pctKeuntungan.toFixed(1)}%`} icon={Percent} tone={pctKeuntungan < 0 ? 'danger' : 'success'} />
                <KpiCard testid="kpi-saldo-sistem" label="Sisa Saldo Saat Ini" value={formatRp(dash.saldo_sistem)} icon={PiggyBank} tone={dash.saldo_sistem < 0 ? 'danger' : 'primary'} accent className="sm:col-span-2" />
                <KpiCard testid="kpi-pct-budget" label="% Budget Terpakai" value={`${dash.pct_budget.toFixed(1)}%`} icon={Percent} tone={dash.pct_budget > 90 ? 'danger' : dash.pct_budget > 70 ? 'warning' : 'primary'} className="sm:col-start-3" />
              </div>
            </div>
          )}

          {activeTab === 'recap' && (
            <div role="tabpanel" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 animate-in-up">
              {['Material','Mobilitas','Gaji Tukang','Gaji Member','Lainnya'].map((cat) => {
                const entry = dash.category_recap.find((c) => c.category === cat) || { total: 0, count: 0, percentage: 0, category: cat };
                return <div key={cat} data-testid={`recap-${cat.toLowerCase().replace(/\s+/g,'-')}`} className="card-fpccs p-4"><div className="text-xs text-subtle-fpccs">{cat}</div><div className="text-lg font-semibold font-mono mt-1">{formatRp(entry.total, { compact: true })}</div><div className="mt-2 h-1 rounded-full bg-[var(--fpccs-surface-3)] overflow-hidden"><div className="h-full bg-[var(--fpccs-primary)]" style={{ width: `${entry.percentage}%` }} /></div><div className="text-[11px] text-subtle-fpccs mt-1">{entry.percentage.toFixed(1)}% • {entry.count} transaksi</div></div>;
              })}
            </div>
          )}

          {activeTab === 'mutasi' && <div role="tabpanel" className="animate-in-up"><MutasiDanaSection pid={pid} reloadKey={reloadKey} /></div>}
          {activeTab === 'profit-sharing' && <ProfitSharingSection pid={pid} nilaiRab={nilaiRab} nilaiKeuntungan={nilaiKeuntungan} />}
          {activeTab === 'charts' && <div role="tabpanel" className="animate-in-up"><WorkspaceCharts data={charts} /></div>}
        </div>
      </section>

      {/* Transactions - always visible */}
      <div data-testid="section-transactions">
        <TransactionTable
          key={reloadKey}
          pid={pid}
          onEdit={openEditTx}
          onChanged={onTxSaved}
          onAddIncome={() => { setEditingTx(null); setOpenIncome(true); }}
          onAddExpense={() => { setEditingTx(null); setOpenExpense(true); }}
          onMaterialConsumption={() => navigate(`/projects/${pid}/material-consumption`)}
          onExport={doExport}
        />
      </div>

      {/* Dialogs */}
      <IncomeDialog open={openIncome} onOpenChange={closeIncome} pid={pid} initial={editingTx} onSaved={onTxSaved} />
      <ExpenseDialog open={openExpense} onOpenChange={closeExpense} pid={pid} initial={editingTx} onSaved={onTxSaved} />
    </div>
  );
}

function WorkspaceTab({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`relative flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-3.5 text-xs font-medium transition-colors sm:px-4 sm:text-sm ${active ? 'text-[var(--fpccs-primary)]' : 'text-muted-fpccs hover:text-[var(--fpccs-text)]'}`}
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
      {active && <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-[var(--fpccs-primary)]" />}
    </button>
  );
}

function KpiCard({ testid, label, value, icon: Icon, tone = 'primary', accent = false, className = '' }) {
  const toneMap = {
    primary: 'text-[var(--fpccs-primary)]',
    success: 'text-[var(--fpccs-success)]',
    danger: 'text-[var(--fpccs-danger)]',
    info: 'text-[var(--fpccs-info)]',
    warning: 'text-[var(--fpccs-warning)]',
    neutral: 'text-[var(--fpccs-text-muted)]',
  };
  return (
    <div data-testid={testid} className={`card-fpccs p-5 relative ${accent ? 'strip-primary' : ''} ${className}`}>
      <div className="flex items-center justify-between text-xs text-subtle-fpccs">
        <span>{label}</span>
        <Icon className={`w-3.5 h-3.5 ${toneMap[tone]}`} />
      </div>
      <div className="mt-2 text-xl sm:text-2xl font-semibold font-mono tracking-tight break-all">{value}</div>
    </div>
  );
}

function IncomeSummaryCard({ items, total }) {
  return (
    <div data-testid="kpi-total-pemasukan" className="card-fpccs p-5 relative strip-primary sm:col-span-2">
      <div className="flex items-center justify-between text-xs text-subtle-fpccs">
        <span>Total Pemasukan</span>
        <TrendingUp className="w-3.5 h-3.5 text-[var(--fpccs-success)]" />
      </div>
      <div className="mt-2 text-xl sm:text-2xl font-semibold font-mono tracking-tight break-all">{formatRp(total)}</div>

      <div className="mt-4 max-h-[190px] overflow-y-auto pr-1">
        {items.length === 0 ? (
          <div className="py-5 text-center text-xs text-subtle-fpccs">Belum ada pemasukan.</div>
        ) : (
          <div className="divide-y divide-[var(--fpccs-border)]">
            {items.map((income) => (
              <div key={income.id} className="grid grid-cols-[88px_minmax(0,1fr)_auto] items-center gap-3 py-2.5 text-xs">
                <span className="text-subtle-fpccs whitespace-nowrap">{formatDate(income.date)}</span>
                <span className="truncate text-muted-fpccs">{income.termin || income.note || 'Pemasukan'}</span>
                <span className="font-mono font-medium text-[var(--fpccs-success)] whitespace-nowrap">{formatRp(income.total)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
